import {
  Button,
  Copy,
  Flex,
  Square,
  Stack,
  SvgCaretDown,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useState } from 'react';
import Popover from 'react-popover'; // https://github.com/littlebits/react-popover
import { subYears } from 'date-fns';
import classnames from 'classnames';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { DatePickerInput } from 'src/components/DatePickerInput/DatePickerInput';

export interface IDateRangeSelectionProps {
  label: string;
  startDateValue: Date;
  endDateValue: Date;
  onApply: (startDate: Date, endDate: Date) => void;
  useInputStyle?: boolean;
}

export default function DateRangeSelection({
  label,
  startDateValue,
  endDateValue,
  onApply,
  useInputStyle,
}: IDateRangeSelectionProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const { isPhone } = useScreenSizes();

  useEffect(() => {
    setStartDate(startDateValue);
    setEndDate(endDateValue);
  }, [startDateValue, endDateValue]);

  function toggle() {
    setIsOpen(!isOpen);
  }

  function apply() {
    setIsOpen(false);
    onApply(startDate, endDate);
  }

  function clear() {
    setIsOpen(false);
    setStartDate(null);
    setEndDate(null);
    onApply(null, null);
  }

  return (
    <>
      <Popover
        className="dateRangePopover"
        isOpen={isOpen}
        tipSize={0.01}
        preferPlace="below"
        onOuterAction={apply}
        body={
          <Square
            data-testid="date-range-selection-popover"
            bg="untintedBackground"
            dimension="2"
            inset="md"
          >
            <Stack y="sm">
              <Flex alignItems="center">
                <Stack x="lg">
                  <Copy variant="body" color="secondaryText">
                    Start
                  </Copy>
                </Stack>

                <DatePickerInput
                  initialDate={startDate}
                  dayPickerProps={{
                    selectedDays: startDate,
                    disabledDays: {
                      before: subYears(new Date(), 1),
                    },
                    numberOfMonths: 1,
                  }}
                  placeholder="MM/DD/YYYY"
                  useFullScreenOverlay={isPhone}
                  name="dateStart"
                  id="dateStart"
                  onDateChange={(selection: Date) => {
                    setStartDate(selection);
                  }}
                />
              </Flex>
            </Stack>

            <Stack y="md">
              <Flex alignItems="center">
                <Stack x="lg" minWidth="32px">
                  <Copy variant="body" color="secondaryText">
                    End
                  </Copy>
                </Stack>

                <DatePickerInput
                  initialDate={endDate}
                  dayPickerProps={{
                    selectedDays: endDate,
                    numberOfMonths: 1,
                  }}
                  placeholder="MM/DD/YYYY"
                  useFullScreenOverlay={isPhone}
                  name="endDate"
                  id="endDate"
                  onDateChange={(selection: Date) => {
                    setEndDate(selection);
                  }}
                />
              </Flex>
            </Stack>

            <Flex justifyContent="space-between">
              <Button
                data-testid="date-range-clear"
                className="dateRangeInput_linkButton"
                onClick={clear}
                variant="tertiary"
                buttonSize="medium"
              >
                Clear
              </Button>

              <Button
                data-testid="date-range-apply"
                className="dateRangeInput_linkButton"
                onClick={apply}
                variant="tertiary"
                buttonSize="medium"
              >
                Apply
              </Button>
            </Flex>
          </Square>
        }
      >
        <Flex
          data-testid="date-range-input"
          className={classnames('dateRangeInput', {
            input: useInputStyle,
          })}
          alignItems="center"
          justifyContent="space-between"
          padding="9px"
          alignContent="center"
          borderRadius="4px"
          bg="untintedBackground"
          onClick={toggle}
          style={{ cursor: 'pointer' }}
        >
          <Flex alignSelf="center">
            <Copy variant="body" color="primaryText">
              {label}
            </Copy>
          </Flex>

          <SvgCaretDown
            className="dateRangeInput_caret"
            size={useInputStyle ? '16px' : '24px'}
          />
        </Flex>
      </Popover>
    </>
  );
}
